import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Slider,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Alert,
    Tabs,
    Tab,
    CircularProgress
} from '@mui/material';
import { SelfImprovement as MindIcon, Restaurant as FoodIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../../config/api';

const MindfulnessLog = () => {
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    // Hunger Log State
    const [hungerLog, setHungerLog] = useState({
        hunger_level_before: '',
        emotion_before: '',
        hunger_level_after: '',
        satisfaction_level: 5,
        notes: ''
    });

    // STOP Practice State
    const [stopLog, setStopLog] = useState({
        stress_level_before: 5,
        stress_level_after: 5,
        observations: ''
    });

    const handleHungerChange = (field, value) => {
        setHungerLog(prev => ({ ...prev, [field]: value }));
        setSuccess(false);
    };

    const handleStopChange = (field, value) => {
        setStopLog(prev => ({ ...prev, [field]: value }));
        setSuccess(false);
    };

    const handleSubmitHunger = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await fetch(`${API_BASE_URL}/mindfulness/hunger`, {
                method: 'POST',
                headers,
                body: JSON.stringify(hungerLog)
            });

            if (!response.ok) throw new Error('Error al guardar');
            setSuccess(true);
            setHungerLog({
                hunger_level_before: '',
                emotion_before: '',
                hunger_level_after: '',
                satisfaction_level: 5,
                notes: ''
            });
        } catch (err) {
            setError('No se pudo guardar el registro.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitStop = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth')).token : null;
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            };

            const response = await fetch(`${API_BASE_URL}/mindfulness/stop`, {
                method: 'POST',
                headers,
                body: JSON.stringify(stopLog)
            });

            if (!response.ok) throw new Error('Error al guardar');
            setSuccess(true);
            setStopLog({
                stress_level_before: 5,
                stress_level_after: 5,
                observations: ''
            });
        } catch (err) {
            setError('No se pudo guardar el registro.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight={700}>
                Mindfulness y Alimentación Consciente
            </Typography>

            <Tabs value={tab} onChange={(e, v) => { setTab(v); setSuccess(false); setError(null); }} sx={{ mb: 3 }}>
                <Tab icon={<FoodIcon />} label="Hambre/Saciedad" />
                <Tab icon={<MindIcon />} label="Práctica STOP" />
            </Tabs>

            {success && <Alert severity="success" sx={{ mb: 2 }}>Registro guardado correctamente</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {tab === 0 && (
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Hambre Antes (1-8)</InputLabel>
                            <Select
                                value={hungerLog.hunger_level_before}
                                label="Hambre Antes (1-8)"
                                onChange={(e) => handleHungerChange('hunger_level_before', e.target.value)}
                            >
                                <MenuItem value={1}>1 - Famélico</MenuItem>
                                <MenuItem value={2}>2 - Muy hambriento</MenuItem>
                                <MenuItem value={3}>3 - Hambriento</MenuItem>
                                <MenuItem value={4}>4 - Neutral</MenuItem>
                                <MenuItem value={5}>5 - Satisfecho</MenuItem>
                                <MenuItem value={6}>6 - Lleno</MenuItem>
                                <MenuItem value={7}>7 - Muy lleno</MenuItem>
                                <MenuItem value={8}>8 - Enfermo</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Emoción Antes"
                            value={hungerLog.emotion_before}
                            onChange={(e) => handleHungerChange('emotion_before', e.target.value)}
                            size="small"
                            placeholder="¿Ansiedad, aburrimiento, tristeza?"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Hambre Después (1-8)</InputLabel>
                            <Select
                                value={hungerLog.hunger_level_after}
                                label="Hambre Después (1-8)"
                                onChange={(e) => handleHungerChange('hunger_level_after', e.target.value)}
                            >
                                <MenuItem value={4}>4 - Neutral</MenuItem>
                                <MenuItem value={5}>5 - Satisfecho</MenuItem>
                                <MenuItem value={6}>6 - Lleno</MenuItem>
                                <MenuItem value={7}>7 - Muy lleno</MenuItem>
                                <MenuItem value={8}>8 - Enfermo</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography gutterBottom variant="caption">Nivel de Satisfacción (1-10)</Typography>
                        <Slider
                            value={hungerLog.satisfaction_level}
                            onChange={(e, val) => handleHungerChange('satisfaction_level', val)}
                            step={1}
                            marks
                            min={1}
                            max={10}
                            valueLabelDisplay="auto"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            onClick={handleSubmitHunger}
                            disabled={loading}
                            fullWidth
                        >
                            {loading ? 'Guardando...' : 'Registrar Comida'}
                        </Button>
                    </Grid>
                </Grid>
            )}

            {tab === 1 && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="body2" paragraph>
                            S: Stop (Para) | T: Take a breath (Respira) | O: Observe (Observa) | P: Proceed (Procede)
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography gutterBottom variant="caption">Estrés Antes (1-10)</Typography>
                        <Slider
                            value={stopLog.stress_level_before}
                            onChange={(e, val) => handleStopChange('stress_level_before', val)}
                            step={1}
                            marks
                            min={1}
                            max={10}
                            valueLabelDisplay="auto"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography gutterBottom variant="caption">Estrés Después (1-10)</Typography>
                        <Slider
                            value={stopLog.stress_level_after}
                            onChange={(e, val) => handleStopChange('stress_level_after', val)}
                            step={1}
                            marks
                            min={1}
                            max={10}
                            valueLabelDisplay="auto"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Observaciones"
                            value={stopLog.observations}
                            onChange={(e) => handleStopChange('observations', e.target.value)}
                            placeholder="¿Qué notaste en tu cuerpo, mente y emociones?"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            onClick={handleSubmitStop}
                            disabled={loading}
                            fullWidth
                            color="secondary"
                        >
                            {loading ? 'Guardando...' : 'Registrar Práctica STOP'}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Paper>
    );
};

export default MindfulnessLog;
